import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Play } from 'lucide-react';
import { LiquidGlassButton } from '../UI/LiquidGlass';

const LandingHero = () => {
    const heroVidRef = useRef(null);
    const [autoplayFailed, setAutoplayFailed] = useState(false);
    useEffect(() => {
        const el = heroVidRef.current;
        if (!el) return;
        // iOS Safari ignores autoplay when the video starts hidden (framer-
        // motion opacity:0). Retry play once the element is visible. If the
        // play() promise rejects (Low Power Mode, "Never autoplay" Safari
        // setting), flip to the tap-to-play overlay below.
        const tryPlay = () => {
            el.play()
                .then(() => setAutoplayFailed(false))
                .catch(() => setAutoplayFailed(true));
        };
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) tryPlay(); },
            { threshold: 0.15 },
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);
    const handleHeroTap = () => {
        const el = heroVidRef.current;
        if (!el) return;
        el.play().then(() => setAutoplayFailed(false)).catch(() => {});
    };
    return (
        <section className="relative min-h-screen flex flex-col items-center justify-start md:justify-center overflow-hidden px-6 pt-24 md:pt-16 pb-20 md:pb-0">
            {/* Ambient blobs */}
            <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-[var(--primary-color)] opacity-[0.06] blur-[140px] rounded-full pointer-events-none" />
            <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-[#0D52BC] opacity-[0.08] blur-[120px] rounded-full pointer-events-none" />
            <div className="absolute top-20 left-10 w-[300px] h-[300px] bg-purple-600 opacity-[0.05] blur-[100px] rounded-full pointer-events-none" />

            {/* Subtle grid */}
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    backgroundImage: 'linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)',
                    backgroundSize: '60px 60px',
                }}
            />

            {/* Headline — order-1 on every breakpoint. */}
            <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.1 }}
                className="relative order-1 text-center text-5xl md:text-7xl font-heading font-black leading-[1.05] tracking-tight max-w-5xl"
            >
                Your unhinged{' '}
                <span
                    className="bg-clip-text text-transparent"
                    style={{ backgroundImage: 'linear-gradient(135deg, var(--primary-color) 0%, #0D52BC 60%, #a855f7 100%)' }}
                >
                    creative companion.
                </span>
            </motion.h1>

            {/* Tagline — third on mobile (after the preview reel), second on
                 desktop (right under the headline, original order). */}
            <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className="relative order-3 md:order-2 mt-6 text-center text-lg md:text-xl text-[var(--text-secondary)] max-w-2xl leading-relaxed"
            >
                Helix is the AI-powered playground where you dream up worlds, generate
                wild visuals, stitch them into stories, and ship something nobody saw coming.
                No film degree required. Just vibes and audacity.
            </motion.p>

            {/* CTAs — LiquidGlass surfaces over the ambient blob backdrop.
                 Primary stays solid cyan for the hottest CTA; secondary uses
                 the frosted glass treatment so it reads as part of the same
                 visual language Helix uses inside the app. */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.32 }}
                className="relative order-4 md:order-3 mt-10 flex flex-col sm:flex-row items-center gap-4"
            >
                <Link
                    to="/signup"
                    className="flex items-center gap-2 px-8 py-4 bg-[var(--primary-color)] text-black font-bold text-sm rounded-xl hover:brightness-110 transition-all shadow-[0_0_24px_rgba(24,181,240,0.4)] hover:shadow-[0_0_40px_rgba(24,181,240,0.55)]"
                >
                    Start Creating Free <ArrowRight size={16} />
                </Link>
                <LiquidGlassButton href="#features">
                    <span className="flex items-center gap-2 text-sm font-semibold text-white">
                        <Play size={14} fill="currentColor" /> See How It Works
                    </span>
                </LiquidGlassButton>
            </motion.div>

            {/* Hero reel preview — second on mobile (immediately under the
                 headline so visitors see the product before reading), last on
                 desktop (under headline + tagline + CTAs, original order). */}
            <motion.div
                initial={{ opacity: 0, y: 60, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="relative order-2 md:order-4 mt-10 md:mt-20 w-full max-w-5xl"
            >
                {/* Glow behind screenshot */}
                <div className="absolute -inset-4 bg-[var(--primary-color)] opacity-[0.08] blur-3xl rounded-3xl pointer-events-none" />

                <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-[0_40px_100px_rgba(0,0,0,0.8)]">
                    {/* Fake browser chrome */}
                    <div className="bg-[rgba(10,14,25,0.95)] border-b border-white/5 px-4 py-3 flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-red-500/60" />
                        <div className="w-3 h-3 rounded-full bg-yellow-500/60" />
                        <div className="w-3 h-3 rounded-full bg-green-500/60" />
                        <div className="flex-1 mx-4 h-6 rounded-md bg-white/5 flex items-center px-3">
                            <span className="text-[10px] text-white/20">helix.eternium.ai</span>
                        </div>
                    </div>

                    {/* Hero reel — 27-second platform walkthrough, pre-rendered
                         to MP4 + WebM by apps/web/scripts/render-reel-scenes.mjs.
                         <video> is dramatically faster than the live React +
                         Babel iframe it replaced (no JIT compile, no React
                         runtime, ~1.7MB vs 45MB asset chain). Re-run the
                         render script after editing scene_hero_reel.jsx. */}
                    <div className="aspect-[16/9] bg-[rgba(5,8,16,0.98)] relative overflow-hidden">
                        <video
                            ref={heroVidRef}
                            autoPlay
                            loop
                            muted
                            playsInline
                            preload="auto"
                            poster="/reel/scenes/hero-reel.jpg"
                            aria-label="Helix hero reel"
                            className="absolute inset-0 w-full h-full object-cover"
                        >
                            <source src="/reel/scenes/hero-reel.mp4" type="video/mp4" />
                            <source src="/reel/scenes/hero-reel.webm" type="video/webm" />
                        </video>
                        {autoplayFailed && (
                            <button
                                type="button"
                                onClick={handleHeroTap}
                                aria-label="Play hero reel"
                                className="absolute inset-0 z-10 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors group"
                            >
                                <div className="w-16 h-16 rounded-full bg-white/90 group-hover:bg-white flex items-center justify-center shadow-lg group-active:scale-95 transition-all">
                                    <Play size={28} className="text-black ml-0.5" fill="currentColor" />
                                </div>
                            </button>
                        )}
                    </div>
                </div>

                {/* Fade at bottom */}
                <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[var(--bg-dark)] to-transparent pointer-events-none" />
            </motion.div>
        </section>
    );
};

export default LandingHero;
